import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchAssetById, updateAsset, resetUpdateStatus } from './assetSlice';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Box,
  Paper,
  Typography,
} from '@mui/material';

const EditAsset = () => {
  const { id } = useParams(); // Asset ID from URL
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { singleAsset, updateStatus, fetchByIdStatus, error } = useSelector((state) => state.asset);

  const [formData, setFormData] = useState({
    name: '',
    symbol: '',
    assetType: '',
    currency: 'USD',
  });

  // Fetch the asset details when the component loads
  useEffect(() => {
    dispatch(fetchAssetById(id));
  }, [dispatch, id]);

  // Populate the formData when the asset is successfully fetched
  useEffect(() => {
    if (fetchByIdStatus === 'succeeded' && singleAsset) {
      setFormData((prev) => ({
        ...prev,
        name: singleAsset.asset?.name || '',
        symbol: singleAsset.asset?.symbol || '',
        assetType: singleAsset.asset?.assetType || '',
        currency: singleAsset.asset?.currency || 'USD',
      }));
    }
  }, [fetchByIdStatus, singleAsset]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(updateAsset({ id, ...formData })).unwrap();
      dispatch(resetUpdateStatus());
      navigate(`/portfolios/${singleAsset.asset.portfolioId}/assets`);
    } catch (err) {
      console.error('Failed to update asset:', err);
    }
  };

  if (fetchByIdStatus === 'loading') {
    return <Typography variant="h6">Loading asset details...</Typography>;
  }

  if (fetchByIdStatus === 'failed') {
    return <Typography variant="h6" color="error">Error: {error}</Typography>;
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
      <Paper elevation={3} sx={{ padding: 4, width: 400 }}>
        <Typography variant="h4" sx={{ marginBottom: 2 }}>Edit Asset</Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Name"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            margin="normal"
          />
          <TextField
            fullWidth
            label="Symbol"
            id="symbol"
            name="symbol"
            value={formData.symbol}
            onChange={handleInputChange}
            required
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="assetType-label">Asset Type</InputLabel>
            <Select
              labelId="assetType-label"
              id="assetType"
              name="assetType"
              value={formData.assetType}
              onChange={handleInputChange}
              required
            >
              <MenuItem value="" disabled>Select an asset type</MenuItem>
              <MenuItem value="stock">Stock</MenuItem>
              <MenuItem value="crypto">Crypto</MenuItem>
              <MenuItem value="real estate">Real Estate</MenuItem>
              <MenuItem value="currency">Currency</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel id="currency-label">Currency</InputLabel>
            <Select
              labelId="currency-label"
              id="currency"
              name="currency"
              value={formData.currency}
              onChange={handleInputChange}
              required
            >
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="GBP">GBP</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
              <MenuItem value="JPY">JPY</MenuItem>
              <MenuItem value="AUD">AUD</MenuItem>
            </Select>
          </FormControl>
          <Button
            fullWidth
            type="submit"
            variant="contained"
            color="primary"
            disabled={updateStatus === 'loading'}
            sx={{ marginTop: 2 }}
          >
            {updateStatus === 'loading' ? 'Updating...' : 'Update Asset'}
          </Button>
        </form>
        {updateStatus === 'failed' && (
          <Typography variant="body2" color="error" sx={{ marginTop: 2 }}>{error}</Typography>
        )}
      </Paper>
    </Box>
  );
};

export default EditAsset;